import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import type { Renderer2 } from '@angular/core';
import { CSP_NONCE, inject, Injectable, PLATFORM_ID, RendererFactory2 } from '@angular/core';

import type { TrustedTypePolicy, TrustedTypesWindow } from 'trusted-types/lib';

import { COOKIE_CONSENT_CONFIG } from './cookie-consent-config';

const GA_SCRIPT_ID = 'arya-google-analytics-loader';
const GTAG_ORIGIN = 'https://www.googletagmanager.com';

type GtagArguments = readonly unknown[];

interface GoogleAnalyticsWindow {
  dataLayer?: GtagArguments[];
  gtag?: (...args: GtagArguments) => void;
}

const ANALYTICS_POLICY_OPTIONS = {
  createScriptURL: (input: string) => {
    if (new URL(input).origin === GTAG_ORIGIN) return input;
    throw new TypeError(`Untrusted analytics script URL: ${input}`);
  },
} satisfies TrustedTypePolicyOptions;

@Injectable({ providedIn: 'root' })
export class GoogleAnalyticsLoader {
  private readonly config = inject(COOKIE_CONSENT_CONFIG);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly nonce = inject(CSP_NONCE, { optional: true });
  private readonly renderer: Renderer2 = inject(RendererFactory2).createRenderer(null, null);
  private policy: Pick<TrustedTypePolicy, 'createScriptURL'> | undefined;
  private enabled = false;

  public enable(): void {
    if (this.enabled) return;
    if (!isPlatformBrowser(this.platformId)) return;
    const { measurementId } = this.config;
    if (measurementId === undefined) return;

    const view = this.document.defaultView as (Window & GoogleAnalyticsWindow) | null;
    if (view === null) return;

    const dataLayer = (view.dataLayer ??= []);
    const gtag =
      view.gtag ??
      (view.gtag = (...args: GtagArguments): void => {
        dataLayer.push(args);
      });
    gtag('js', new Date());
    gtag('config', measurementId);

    const loader = this.document.createElement('script');
    this.renderer.setAttribute(loader, 'id', GA_SCRIPT_ID);
    this.renderer.setAttribute(loader, 'async', '');
    this.renderer.setProperty(loader, 'src', this.trustedScriptUrl(`${GTAG_ORIGIN}/gtag/js?id=${measurementId}`));
    this.applyNonce(loader);
    this.renderer.appendChild(this.document.head, loader);

    this.enabled = true;
  }

  public disable(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const loader = this.document.querySelector(`#${GA_SCRIPT_ID}`);
    if (loader !== null) this.renderer.removeChild(loader.parentNode, loader);
    this.enabled = false;
  }

  private trustedScriptUrl(url: string): string | TrustedScriptURL {
    const view: TrustedTypesWindow | null = this.document.defaultView;
    const trustedTypes = view?.trustedTypes;
    if (!trustedTypes?.createPolicy) return url;

    const policy = (this.policy ??= trustedTypes.createPolicy('analytics', ANALYTICS_POLICY_OPTIONS));
    return policy.createScriptURL(url);
  }

  private applyNonce(element: HTMLScriptElement): void {
    if (this.nonce === null) return;
    element.nonce = this.nonce;
    this.renderer.setAttribute(element, 'nonce', this.nonce);
  }
}
